import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Card, Statistic, Row, Col, Select, message, Tooltip } from 'antd';
import { EyeOutlined, ReloadOutlined, FileTextOutlined } from '@ant-design/icons';
import { getAll, getEstadisticas } from '../../../../apis/SolicitudDocumentoApi';
import EditSolicitudDocumento from './Edit';
import moment from 'moment';

const { Option } = Select;

const SolicitudDocumentosPage = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstatus, setFiltroEstatus] = useState(null);
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    pendientes: 0,
    en_proceso: 0,
    aprobadas: 0,
    rechazadas: 0
  });
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    cargarSolicitudes();
    cargarEstadisticas();
  }, [filtroEstatus]);

  const cargarSolicitudes = async () => {
    setLoading(true);
    try {
      const response = await getAll(filtroEstatus);
      if (response.ok) {
        setSolicitudes(response.payload);
      } else {
        message.error('Error al cargar las solicitudes');
      }
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      message.error('Error al cargar las solicitudes');
    }
    setLoading(false);
  };

  const cargarEstadisticas = async () => {
    try {
      const response = await getEstadisticas();
      if (response.ok) {
        setEstadisticas(response.payload);
      }
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const handleVerDetalle = (solicitud) => {
    setSelectedSolicitud(solicitud);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedSolicitud(null);
    cargarSolicitudes();
    cargarEstadisticas();
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Empleado',
      dataIndex: 'empleado_nombre',
      key: 'empleado_nombre',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.empleado_email}</div>
        </div>
      ),
    },
    {
      title: 'Tipo de Documento',
      dataIndex: 'tipo_documento',
      key: 'tipo_documento',
      filters: [
        { text: 'Constancia Laboral', value: 'constancia_laboral' },
        { text: 'Carta de Recomendación', value: 'carta_recomendacion' },
        { text: 'Comprobante de Ingresos', value: 'comprobante_ingresos' },
        { text: 'Historial de Nómina', value: 'historial_nomina' },
        { text: 'Certificado de Antigüedad', value: 'certificado_antiguedad' },
        { text: 'Otro', value: 'otro' },
      ],
      onFilter: (value, record) => record.tipo_documento === value,
      render: (text) => {
        const labels = {
          constancia_laboral: 'Constancia Laboral',
          carta_recomendacion: 'Carta de Recomendación',
          comprobante_ingresos: 'Comprobante de Ingresos',
          historial_nomina: 'Historial de Nómina',
          certificado_antiguedad: 'Certificado de Antigüedad',
          otro: 'Otro'
        };
        return labels[text] || text;
      },
    },
    {
      title: 'Fecha de Solicitud',
      dataIndex: 'fecha_solicitud',
      key: 'fecha_solicitud',
      width: 150,
      sorter: (a, b) => moment(a.fecha_solicitud).unix() - moment(b.fecha_solicitud).unix(),
      render: (text) => moment(text).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Estatus',
      dataIndex: 'estatus',
      key: 'estatus',
      width: 130,
      align: 'center',
      filters: [
        { text: 'Pendiente', value: 'Pendiente' },
        { text: 'En Proceso', value: 'En Proceso' },
        { text: 'Aprobado', value: 'Aprobado' },
        { text: 'Rechazado', value: 'Rechazado' },
      ],
      onFilter: (value, record) => record.estatus === value,
      render: (estatus) => {
        const config = {
          'Pendiente': { color: 'blue', text: 'Pendiente' },
          'En Proceso': { color: 'orange', text: 'En Proceso' },
          'Aprobado': { color: 'green', text: 'Aprobado' },
          'Rechazado': { color: 'red', text: 'Rechazado' },
        };
        const { color, text } = config[estatus] || { color: 'default', text: estatus };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Fecha de Respuesta',
      dataIndex: 'fecha_respuesta',
      key: 'fecha_respuesta',
      width: 150,
      render: (text) => text ? moment(text).format('DD/MM/YYYY HH:mm') : <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalle y gestionar">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalle(record)}
            >
              Ver
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Estadísticas */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total"
              value={estadisticas.total}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#2E2D75' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="Pendientes"
              value={estadisticas.pendientes}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="En Proceso"
              value={estadisticas.en_proceso}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="Aprobadas"
              value={estadisticas.aprobadas}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={5}>
          <Card>
            <Statistic
              title="Rechazadas"
              value={estadisticas.rechazadas}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabla de solicitudes */}
      <Card
        title="Gestión de Solicitudes de Documentos"
        extra={
          <Space>
            <Select
              placeholder="Filtrar por estatus"
              allowClear
              style={{ width: 180 }}
              onChange={setFiltroEstatus}
              value={filtroEstatus}
            >
              <Option value="Pendiente">Pendiente</Option>
              <Option value="En Proceso">En Proceso</Option>
              <Option value="Aprobado">Aprobado</Option>
              <Option value="Rechazado">Rechazado</Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={cargarSolicitudes}
              loading={loading}
            >
              Actualizar
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={solicitudes}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} solicitudes`,
            pageSizeOptions: ['10', '15', '20', '50'],
          }}
        />
      </Card>

      {/* Modal de detalle y gestión */}
      {modalVisible && (
        <EditSolicitudDocumento
          visible={modalVisible}
          solicitud={selectedSolicitud}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default SolicitudDocumentosPage;
